import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import Confirm from 'components/Confirm';
import Message from 'components/Message';
import { closeModal } from 'components/Modal';
import { bem } from 'helpers/class-name';

import messages from '../ModifierGroupEditor.messages';
import './EditSharedGroupModal.scss';

const { block, element } = bem('edit-shared-group-modal');

export const EDIT_SHARED_GROUP_MODAL_NAME = 'EditSharedGroupModal';

const EditSharedGroupModal = ({ groupName, itemNames, onConfirmSave }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);

  const handleCancel = () => {
    if (!isSaving) {
      dispatch(closeModal());
    }
  };

  const handleConfirm = async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    try {
      // The save hook reports failure through the editor's own error surface, so the dialog
      // closes either way instead of duplicating it.
      await onConfirmSave?.();
    } finally {
      setIsSaving(false);
      dispatch(closeModal());
    }
  };

  return (
    <div
      className={block()}
      role="dialog"
      aria-modal="true"
      aria-label={intl.formatMessage(messages.editSharedDialogLabel)}>
      <Confirm
        className={element('content')}
        title={
          <Message
            message={messages.editSharedTitle}
            values={{ groupName, strong: chunks => <strong>{chunks}</strong> }}
          />
        }
        cancelNode={<Message message={messages.editSharedCancel} />}
        acceptNode={<Message message={messages.editSharedAction} />}
        acceptDisabled={isSaving}
        acceptWaiting={isSaving}
        onCancel={handleCancel}
        onRequestClose={handleCancel}
        onConfirm={handleConfirm}>
        <p className={element('line')}>
          <Message
            message={messages.editSharedUsedIn}
            values={{
              itemCount: itemNames.length,
              itemNames: intl.formatList(itemNames, { type: 'conjunction' }),
            }}
          />
        </p>
      </Confirm>
    </div>
  );
};

EditSharedGroupModal.propTypes = {
  groupName: PropTypes.string,
  itemNames: PropTypes.arrayOf(PropTypes.string),
  onConfirmSave: PropTypes.func,
};

EditSharedGroupModal.defaultProps = {
  groupName: '',
  itemNames: [],
};

export default EditSharedGroupModal;
